import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
  RecaptchaVerifier,
  sendPasswordResetEmail,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  Auth,
  ConfirmationResult
} from 'firebase/auth';
import { getAnalytics, Analytics } from 'firebase/analytics';

// Firebase configuration with actual project values
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || 'AIzaSyBAaonKoxscGfSh03Nfbb5SV-WMo--zKbU',
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || 'herzens-panel.firebaseapp.com',
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || 'herzens-panel',
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || 'herzens-panel.appspot.com',
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '29399266325',
  appId: process.env.REACT_APP_FIREBASE_APP_ID || '1:29399266325:web:7d038c2cd87af854258a1c',
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 'G-562DWSTX6T'
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth: Auth = getAuth(app);

// Initialize Analytics (optional, only if measurement ID is provided)
let analytics: Analytics | null = null;
if (firebaseConfig.measurementId && typeof window !== 'undefined') {
  try {
    analytics = getAnalytics(app);
  } catch (error) {
    console.warn('Analytics initialization failed:', error);
  }
}

export { analytics };

// Authentication functions
export const signInUser = async (email: string, password: string) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error: any) {
    const code: string = error.code || '';
    if (
      code === 'auth/invalid-credential' ||
      code === 'auth/wrong-password' ||
      code === 'auth/user-not-found' ||
      code === 'auth/invalid-email'
    ) {
      throw new Error('E-Mail-Adresse oder Passwort ist falsch. Bitte überprüfen Sie Ihre Eingaben oder setzen Sie Ihr Passwort über „Passwort vergessen?" zurück.');
    }
    if (code === 'auth/user-disabled') {
      throw new Error('Dieses Konto wurde deaktiviert. Bitte wenden Sie sich an Ihren Administrator.');
    }
    if (code === 'auth/too-many-requests') {
      throw new Error('Zu viele Anmeldeversuche. Das Konto wurde vorübergehend gesperrt. Bitte setzen Sie Ihr Passwort zurück oder versuchen Sie es später erneut.');
    }
    throw new Error(error.message || 'Anmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.');
  }
};

// Phone authentication functions
export const setupRecaptcha = (elementId: string): RecaptchaVerifier => {
  const recaptchaVerifier = new RecaptchaVerifier(auth, elementId, {
    'size': 'normal',
    'callback': () => {
      // reCAPTCHA solved, allow signInWithPhoneNumber
    },
    'expired-callback': () => {
      // Response expired. Ask user to solve reCAPTCHA again.
    }
  });
  return recaptchaVerifier;
};

export const sendPhoneVerification = async (
  phoneNumber: string, 
  recaptchaVerifier: RecaptchaVerifier
): Promise<ConfirmationResult> => {
  try {
    const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
    return confirmationResult;
  } catch (error: any) {
    throw new Error(error.message || 'Phone verification failed');
  }
};

export const verifyPhoneCode = async (
  confirmationResult: ConfirmationResult,
  verificationCode: string
): Promise<FirebaseUser> => {
  try {
    const result = await confirmationResult.confirm(verificationCode);
    return result.user;
  } catch (error: any) {
    throw new Error(error.message || 'Code verification failed');
  }
};

// Alternative phone auth method using PhoneAuthProvider
export const createPhoneCredential = (verificationId: string, verificationCode: string) => {
  return PhoneAuthProvider.credential(verificationId, verificationCode);
};

export const signInWithPhoneCredential = async (credential: any): Promise<FirebaseUser> => {
  try {
    const result = await signInWithCredential(auth, credential);
    return result.user;
  } catch (error: any) {
    throw new Error(error.message || 'Phone authentication failed');
  }
};

export const resetUserPassword = async (email: string): Promise<void> => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error: any) {
    throw new Error(error.message || 'Password reset failed');
  }
};

export const signInWithCustomTokenAuth = async (token: string): Promise<FirebaseUser> => {
  const userCredential = await signInWithCustomToken(auth, token);
  return userCredential.user;
};

export const signOutUser = async () => {
  try {
    await signOut(auth);
  } catch (error: any) {
    throw new Error(error.message || 'Logout failed');
  }
};

export const onAuthStateChange = (callback: (user: FirebaseUser | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Get current user's ID token
export const getCurrentUserToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (user) {
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting user token:', error);
      return null;
    }
  }
  return null;
};

// Get current user's phone number
export const getCurrentUserPhoneNumber = (): string | null => {
  const user = auth.currentUser;
  return user?.phoneNumber || null;
};

export default app; 